import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import GlobalModalComponent from "../globalModal/GlobalModalComponent";
import CardSelectModal from "../card-select-modal/CardSelectModal";
import PaymentStatusModal from "../payment-status-modal/PaymentStatusModal";
import NoCardsModal from "../no-cards-modal/NoCardsModal";
import MainButton from "../button/MainButton";
import { BankCardsServices } from "../../../services/bankCards.service";
import { ServiceDebtService } from "../../../services/serviceDebt.service";
import { RootState } from "../../../redux/store";
import { BankCardType } from "../../../interface/site.interface";
import styles from "./ServiceDebtPayModal.module.scss";
import closeIcon from "../../../assets/images/referal/Close Icon.svg";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Total amount to display, in AZN */
  amountAzn: number;
  /** If set, only debts for this package are paid */
  packageId?: number;
  /** If set, only these specific debt IDs are paid */
  debtIds?: number[];
  onSuccess?: () => void;
}

const ServiceDebtPayModal = ({
  isOpen,
  onClose,
  amountAzn,
  packageId,
  debtIds,
  onSuccess,
}: Props) => {
  const { data: t } = useSelector((state: RootState) => state.translationData);

  const [isPaying, setIsPaying] = useState(false);
  const [cards, setCards] = useState<BankCardType[]>([]);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [noCardsOpen, setNoCardsOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [payErrorMsg, setPayErrorMsg] = useState("");

  const handleSuccess = () => {
    setPaySuccess(true);
    setStatusModalOpen(true);
    onSuccess?.();
  };

  const handlePayBalance = async () => {
    setIsPaying(true);
    try {
      await ServiceDebtService.payWithBalance(debtIds, packageId);
      onClose();
      handleSuccess();
    } catch (err: any) {
      toast.error(err?.content || err?.message || "Xəta baş verdi");
    } finally {
      setIsPaying(false);
    }
  };

  const handlePayCardClick = async () => {
    setIsPaying(true);
    try {
      const res = await BankCardsServices.getBankCards();
      const list: BankCardType[] = res.data?.data || [];
      if (list.length === 0) {
        onClose();
        setNoCardsOpen(true);
        return;
      }
      setCards(list);
      onClose();
      setCardModalOpen(true);
    } catch {
      onClose();
      setNoCardsOpen(true);
    } finally {
      setIsPaying(false);
    }
  };

  const handleCardSelect = async (cardId: number) => {
    setIsCardLoading(true);
    try {
      await ServiceDebtService.payWithCard(cardId, debtIds, packageId);
      handleSuccess();
    } catch (err: any) {
      setPayErrorMsg(err?.content || err?.message || "Ödəniş uğursuz");
      setPaySuccess(false);
      setStatusModalOpen(true);
    } finally {
      setIsCardLoading(false);
      setCardModalOpen(false);
    }
  };

  const handlePayDirect = async () => {
    setIsPaying(true);
    try {
      const res = await ServiceDebtService.payDirect(debtIds, packageId);
      const d = res.data;
      if (d?.case === "success" && d?.url) {
        window.location.href = d.url;
      } else {
        toast.error(d?.content || "Xəta baş verdi");
      }
    } catch (err: any) {
      toast.error(err?.content || "Xəta baş verdi");
    } finally {
      setIsPaying(false);
      onClose();
    }
  };

  return (
    <>
      <GlobalModalComponent isOpen={isOpen} onClose={onClose}>
        <div className={styles.modal}>
          <div className={styles.close} onClick={onClose}>
            <img src={closeIcon} alt="x" />
          </div>

          <h3 className={styles.title}>
            {t?.service_debts_payment_title || "Xidmət borcu ödənişi"}
          </h3>

          <div className={styles.amountCard}>
            <div className={styles.amountRow}>
              <span className={styles.amountLabel}>
                {t?.service_debts_amount_label || "Məbləğ"}:
              </span>
              <span className={styles.amountValue}>
                {amountAzn.toFixed(2)} AZN
              </span>
            </div>
          </div>

          <div className={styles.buttons}>
            <MainButton
              className={styles.balanceBtn}
              text={isPaying ? "..." : t?.pay_from_balance || "Balansdan ödəniş et"}
              onClick={handlePayBalance}
              disabled={isPaying}
            />
            <MainButton
              className={styles.cardBtn}
              text={isPaying ? "..." : t?.pay_by_card || "Kartla ödəniş et"}
              onClick={handlePayCardClick}
              disabled={isPaying}
            />
            <MainButton
              className={styles.directBtn}
              text={isPaying ? "..." : t?.direct_pay || "Birbaşa ödə"}
              onClick={handlePayDirect}
              disabled={isPaying}
            />
          </div>
        </div>
      </GlobalModalComponent>

      <CardSelectModal
        isOpen={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        onSelectCard={handleCardSelect}
        cards={cards}
        isLoading={isCardLoading}
        amount={`${amountAzn.toFixed(2)} AZN`}
        translationData={t}
      />

      <PaymentStatusModal
        isOpen={statusModalOpen}
        isSuccess={paySuccess}
        onClose={() => setStatusModalOpen(false)}
        onRedirect={() => setStatusModalOpen(false)}
        errorMessage={payErrorMsg}
        translationData={t}
      />

      <NoCardsModal
        isOpen={noCardsOpen}
        onClose={() => setNoCardsOpen(false)}
        translationData={t}
      />
    </>
  );
};

export default ServiceDebtPayModal;
